import { useCallback, useEffect, useState } from 'react'
import { useApiFormAuthInvite } from '../../api-new/state/auth'
import { useAuthContext } from '../../context/auth.context'

// The signed-in login's pending invitations (GET /auth-invite: bound to the
// session, never an invite key; an impersonated session gets none). Anything
// but a list -- an older auth service, a failure, no session -- counts as
// none, so the account menu and the Switch organization page never break on
// it.
//
// One shared, cached read (re-review 5 RM5-3): every component showing the
// count (account menu badge, Switch organization notice, learner landing)
// reads the same cache instead of querying on each mount or navigation. It
// re-reads when it is older than FRESH_MS and the tab regains focus, and
// `reload` (after an Accept / Decline) re-reads at once and updates every
// subscriber.

export const FRESH_MS = 60 * 1000

const cache = { key: null, invites: [], at: 0, loaded: false, inflight: null, seq: 0 }
const listeners = new Set()

const publish = () => listeners.forEach(listener => listener())

// Tests: start every case from an empty cache.
export const resetPendingInvitesCache = () => {
  Object.assign(cache, { key: null, invites: [], at: 0, loaded: false, inflight: null, seq: cache.seq + 1 })
  publish()
}

// A read. force (after an Accept / Decline) always starts a NEW request,
// even while one is in flight -- that one may have left before the answer
// and would return the stale list (re-review 6 RM6-2). Only the newest
// request may write the cache, so an older one finishing last never
// overwrites it; callers of the older one get the newest answer.
const read = async (api, key, force) => {
  if (cache.key !== key) {
    Object.assign(cache, { key, invites: [], at: 0, loaded: false, inflight: null, seq: cache.seq + 1 })
  }

  if (!force && cache.loaded && Date.now() - cache.at < FRESH_MS) {
    return cache.invites
  }

  if (!force && cache.inflight) {
    return cache.inflight
  }

  const seq = ++cache.seq

  const request = (async () => {
    let invites = []
    try {
      const result = await api.get()
      invites = Array.isArray(result) ? result.filter(i => i && i.userID) : []
    } catch {
      invites = []
    }
    if (cache.key === key && cache.seq === seq) {
      Object.assign(cache, { invites, at: Date.now(), loaded: true, inflight: null })
      publish()
      return invites
    }
    // Superseded by a newer read: answer with that one.
    return cache.key === key && cache.inflight ? cache.inflight : invites
  })()

  cache.inflight = request
  return request
}

export const usePendingInvites = ({ enabled = true } = {}) => {
  const apiFormAuthInvite = useApiFormAuthInvite()
  const authContext = useAuthContext()
  const state = (authContext && authContext.state) || {}
  const signedIn = !!state.accessToken
  const key = signedIn && enabled ? String(state.loginID ?? state.userID ?? 'session') : null

  const snapshot = () => (key && cache.key === key ? { loaded: cache.loaded, invites: cache.invites } : { loaded: !key, invites: [] })
  const [view, setView] = useState(snapshot)

  useEffect(
    () => {
      const listener = () => setView(snapshot())
      listeners.add(listener)
      listener()
      return () => listeners.delete(listener)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  )

  const load = useCallback(
    force => {
      if (!key) return Promise.resolve([])
      return read(apiFormAuthInvite, key, force)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  )

  // First use (or a stale cache): read; otherwise the cache answers.
  useEffect(
    () => {
      load(false)
    },
    [load]
  )

  // Back to the tab: re-read if the cache is stale.
  useEffect(
    () => {
      if (!key) return undefined
      const onFocus = () => load(false)
      window.addEventListener('focus', onFocus)
      return () => window.removeEventListener('focus', onFocus)
    },
    [key, load]
  )

  const reload = useCallback(() => load(true), [load])

  return { loaded: view.loaded, invites: view.invites, count: view.invites.length, reload }
}

export const invitationsText = count => (count === 1 ? 'You have 1 invitation.' : `You have ${count} invitations.`)
